import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, KeyRound, Loader2, ShieldAlert, CheckCircle2, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { setSiteLang } from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";
import Stepper, { STEP_LABELS } from "@/components/wizard/Stepper";
import StepPersonal, { validatePersonal } from "@/components/wizard/StepPersonal";
import StepAddresses, { emptyAddress, validateAddresses } from "@/components/wizard/StepAddresses";
import StepConsent, { validateConsent } from "@/components/wizard/StepConsent";
import StepReview from "@/components/wizard/StepReview";
import StepPayment, { validatePayment, money } from "@/components/wizard/StepPayment";
import { YesNo } from "@/components/wizard/StepConsent";
import DowIntro, { validateDowIntro } from "@/components/wizard/dow/DowIntro";
import DowCourt, { validateDowCourt } from "@/components/wizard/dow/DowCourt";
import DowAuth, { validateDowAuth } from "@/components/wizard/dow/DowAuth";
import { DOW_LABELS } from "@/components/wizard/dow/shared";
import { useParams, useSearchParams } from "react-router-dom";
import { Languages } from "lucide-react";
import { useSpanish, setOrgSub } from "@/lib/translate";
import { RequestNewCode } from "@/components/wizard/RequestNewCode";

function LangToggle() {
  const [es, setEs] = useState(() => localStorage.getItem("irs_lang") === "es");
  useSpanish(es);
  const flip = () => { setSiteLang(es ? "en" : "es"); setEs(!es); };
  return (
    <>
      {es && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-[#1B365D] px-4 py-2 text-center text-xs text-white" data-testid="spanish-governing-note" translate="no">
          Traducción al español proporcionada por conveniencia. La versión en inglés es la que rige. / Spanish translation provided for convenience; the English version governs.
        </div>
      )}
      <button type="button" onClick={flip} data-testid="language-toggle-button" translate="no"
        className="fixed bottom-12 right-4 z-50 flex items-center gap-2 rounded-full bg-[#F26B4F] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_-10px_rgba(242,107,79,0.8)] transition-transform hover:-translate-y-0.5 sm:right-6">
        <Languages className="h-4 w-4" /> {es ? "English" : "Español"}
      </button>
    </>
  );
}

export default function Apply() {
  return (
    <>
      <ApplyInner />
      <LangToggle />
    </>
  );
}

const STORE = "irs_volunteer_session";
type Session = { code: string; token: string };
type AppState = { id: string; current_step: number; data: any; lockout_at: string; org_name?: string; payment_required?: boolean; fee?: number; form_type?: string; state_auth?: boolean; locations?: string[]; location_locked?: boolean; participation?: string };

function OrgGate({ slug, onStart, onCode }: { slug: string; onStart: () => Promise<void>; onCode: (code: string, otp?: string) => Promise<any> }) {
  const [org, setOrg] = useState<{ name: string; fee: number; code_or_pay?: boolean } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/public/org/${slug}`).then((r) => setOrg(r.data)).catch((e) => setError(errMsg(e))); }, [slug]);
  const go = async () => {
    setBusy(true);
    try { await onStart(); } catch (err) { setError(errMsg(err)); }
    setBusy(false);
  };
  if (org?.code_or_pay)
    return (
      <CodeGate onStart={onCode} title={org.name} alt={
        <div className="mt-6 border-t border-slate-200 pt-6" data-testid="code-or-pay-alt">
          <p className="text-[15px] text-[#475569]"><b>No code?</b> You can pay the <b>{money(org.fee)}</b> screening fee by card at the end of the application instead.</p>
          {error && <p className="mt-2 text-sm text-[#991B1B]" data-testid="org-apply-error">{error}</p>}
          <button type="button" disabled={busy} onClick={go} data-testid="code-or-pay-pay-button" className="btn-secondary mt-4 h-12 w-full text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Continue and pay at the end <ArrowRight className="h-5 w-5" /></>}
          </button>
        </div>
      } />
    );
  return (
    <div className="mx-auto max-w-xl px-4 pt-16">
      <div className="card-soft fade-up p-8 sm:p-10" data-testid="org-apply-gate">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F26B4F] text-white shadow-[0_10px_20px_-8px_rgba(242,107,79,0.6)]"><KeyRound className="h-7 w-7" /></span>
        <h1 className="mt-6 text-3xl font-extrabold tracking-tight sm:text-4xl">{org ? org.name : "Background check"}</h1>
        <p className="mt-3 text-lg text-[#475569]">Background Check Online Application</p>
        {org && <p className="mt-4 text-[15px] text-[#475569]" data-testid="org-apply-fee">The screening fee of <b>{money(org.fee)}</b> is paid by card at the end, before you submit.</p>}
        {error && (
          <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="org-apply-error">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {error}
          </div>
        )}
        <button type="button" disabled={busy || !org} onClick={go} data-testid="org-apply-begin-button" className="btn-primary mt-6 h-14 w-full bg-[#F26B4F] text-lg hover:bg-[#E0553A]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Begin <ArrowRight className="h-5 w-5" /></>}
        </button>
        <ul className="mt-8 space-y-2 text-sm text-[#64748B]">
          <li className="flex gap-2"><Clock className="h-4 w-4 shrink-0 text-[#F26B4F]" /> Once you begin, you have 24 hours to finish.</li>
        </ul>
      </div>
    </div>
  );
}

function useCountdown(until?: string) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  if (!until) return "";
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return "0m";
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
}

function CodeGate({ onStart, title, alt }: { onStart: (code: string, otp?: string) => Promise<any>; title?: string; alt?: React.ReactNode }) {
  const saved: Session | null = JSON.parse(localStorage.getItem(STORE) || "null");
  const [code, setCode] = useState(saved?.code || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState("");
  const [verify, setVerify] = useState<{ email_hint: string } | null>(null);
  const [otp, setOtp] = useState("");
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setBlocked("");
    try {
      const r = await onStart(code, verify ? otp : undefined);
      if (r?.verify_required) { setVerify({ email_hint: r.email_hint }); setOtp(""); }
    } catch (err: any) {
      setError(errMsg(err));
      if (verify && err?.response?.status === 410) setVerify(null);
      if (!verify && [409, 410].includes(err?.response?.status)) setBlocked(code);
    }
    setBusy(false);
  };
  return (
    <div className="mx-auto max-w-xl px-4 pt-16">
      <form onSubmit={go} className="card-soft fade-up p-8 sm:p-10" data-testid="volunteer-code-form">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F26B4F] text-white shadow-[0_10px_20px_-8px_rgba(242,107,79,0.6)]"><KeyRound className="h-7 w-7" /></span>
        <h1 className="mt-6 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="code-gate-title">{title || "Start your application"}</h1>
        <p className="mt-3 text-lg text-[#475569]">{title ? "Have an entry code? Enter it below to start." : "Enter the authorization code your organization gave you."}</p>
        <label className="field-label mt-8" htmlFor="code">Your code</label>
        <input
          id="code"
          data-testid="volunteer-code-input"
          className="field-input h-16 text-center font-mono text-2xl uppercase tracking-[0.2em]"
          placeholder="e.g. K7M2Q9XA"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setVerify(null); }}
          autoComplete="off"
          required
        />
        {verify && (
          <div className="mt-5 rounded-xl border border-[#CCFBF1] bg-[#F0FDFA] p-4" data-testid="device-verify-box">
            <p className="text-[15px] text-[#134E4A]"><b>Continuing on a different device?</b> To protect your information, we emailed a 6-digit security code to <b data-testid="device-verify-email">{verify.email_hint}</b>. Enter it below to pick up where you left off.</p>
            <input className="field-input mt-3 h-14 text-center font-mono text-2xl tracking-[0.4em]" inputMode="numeric" maxLength={6} placeholder="000000" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} autoComplete="one-time-code" data-testid="device-verify-otp-input" />
            <p className="mt-2 text-xs text-[#64748B]">Didn't get it? Check your spam folder, or clear the box and click Continue my application to send a new code.</p>
          </div>
        )}
        {error && (
          <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="volunteer-code-error">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {error}
          </div>
        )}
        {blocked && blocked === code && <RequestNewCode key={blocked} code={blocked} />}
        <button type="submit" disabled={busy || code.length < 4 || (!!verify && otp.length > 0 && otp.length < 6)} data-testid="volunteer-code-submit-button" className="btn-primary mt-6 h-14 w-full bg-[#F26B4F] text-lg hover:bg-[#E0553A]">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : verify ? "Continue my application" : saved && saved.code === code ? "Resume my application" : "Begin"} {!busy && <ArrowRight className="h-5 w-5" />}
        </button>
        {alt}
        <ul className="mt-8 space-y-2 text-sm text-[#64748B]">
          <li className="flex gap-2"><Clock className="h-4 w-4 shrink-0 text-[#F26B4F]" /> Once you begin, you have 24 hours to finish.</li>
          <li className="flex gap-2"><ShieldAlert className="h-4 w-4 shrink-0 text-[#14B8A6]" /> Switching devices? Enter your code again and we'll email you a security code to continue.</li>
        </ul>
      </form>
    </div>
  );
}

function ApplyInner() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const orgStore = `irs_org_session_${slug}`;
  const [session, setSession] = useState<Session | null>(null);
  const [app, setApp] = useState<AppState | null>(null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState<any>({ personal: {}, addresses: [emptyAddress()], consent: {}, dow: {} });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const left = useCountdown(app?.lockout_at);

  const load = (r: any, s: Session, key: string) => {
    localStorage.setItem(key, JSON.stringify(s));
    setSession(s);
    setApp(r.application);
    const d = r.application.data || {};
    setData({ personal: d.personal || {}, addresses: d.addresses?.length ? d.addresses : [emptyAddress()], consent: d.consent || {}, dow: d.dow || {} });
    setStep(r.application.current_step || 0);
    if (r.resumed) toast.success("Welcome back — your progress was restored.");
  };

  const startOrg = async () => {
    const saved: Session | null = JSON.parse(localStorage.getItem(orgStore) || "null");
    const { data: r } = await api.post(`/public/org/${slug}/start`, { session_token: saved?.token });
    load(r, { code: "", token: r.session_token }, orgStore);
  };

  useEffect(() => {
    const paid = params.get("paid");
    if (!slug || !paid) {
      if (slug && params.get("cancelled")) {
        toast.error("Payment was cancelled. Your application has not been submitted.");
        setParams({});
        startOrg().catch(() => {});
      }
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data: r } = await api.get(`/public/application/payment/${paid}`);
        if (r.payment_status === "paid") {
          localStorage.removeItem(orgStore);
          return setDone(r.reference);
        }
      } catch { /* retry */ }
      if (++tries < 10) setTimeout(poll, 2000);
      else toast.error("We couldn't confirm your payment yet. Please refresh this page in a minute.");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (code: string, otp?: string) => {
    const saved: Session | null = JSON.parse(localStorage.getItem(STORE) || "null");
    const token = saved && saved.code.replace(/[^A-Z0-9]/g, "") === code.replace(/[^A-Z0-9]/g, "") ? saved.token : undefined;
    const { data: r } = await api.post("/public/code/start", { code, session_token: token, otp: otp || undefined });
    if (r.verify_required) return r;
    const s = { code, token: r.session_token };
    localStorage.setItem(STORE, JSON.stringify(s));
    setSession(s);
    setApp(r.application);
    const d = r.application.data || {};
    setData({ personal: d.personal || {}, addresses: d.addresses?.length ? d.addresses : [emptyAddress()], consent: d.consent || {}, dow: d.dow || {} });
    setStep(r.application.current_step || 0);
    if (r.resumed) toast.success("Welcome back — your progress was restored.");
  };

  const isDow = app?.form_type === "dow";
  const isNch = app?.form_type === "nch";
  setOrgSub(isDow ? "" : app?.org_name || "");
  const labels = isDow ? DOW_LABELS : isNch ? ["Getting started", "About you", "Where you've lived", "Authorization", "Review & submit"] : STEP_LABELS;
  const last = labels.length - 1;
  const foreign = (data.addresses || []).some((a: any) => a.country && a.country !== "United States");
  const payV = () => (app?.payment_required ? validatePayment(data.consent) : null);
  const validators = useMemo(() => isDow
    ? [() => validateDowIntro(data.dow), () => validatePersonal(data.personal),
      () => validateAddresses(data.addresses), () => validateDowCourt(data.consent, data.dow), () => validateDowAuth(data.consent, data.dow, foreign), payV]
    : [() => validateDowIntro(data.dow, !!session?.code), () => validatePersonal(data.personal, isNch), () => validateAddresses(data.addresses),
      () => validateConsent(data.consent, data.dow, foreign, isNch, !!app?.state_auth), payV],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, app]);

  const save = async (nextStep: number) => {
    setSaving(true);
    try {
      const { data: r } = await api.put("/public/application", { session_token: session!.token, current_step: nextStep, data, lang: localStorage.getItem("irs_lang") || "en" });
      setData((d: any) => ({ ...d, personal: r.data.personal, ...(r.data.dow ? { dow: r.data.dow } : {}) }));
      setStep(nextStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setSaving(false);
  };

  const next = () => {
    const err = validators[step]();
    if (err) return toast.error(err);
    save(step + 1);
  };

  const submit = async () => {
    for (const [i, v] of validators.entries()) {
      const err = v();
      if (err) {
        if (i < last) setStep(i);
        return toast.error(err);
      }
    }
    setSaving(true);
    if (app?.payment_required) {
      try {
        const { data: r } = await api.post("/public/application/checkout", { session_token: session!.token, current_step: last, data, lang: localStorage.getItem("irs_lang") || "en", origin_url: window.location.origin, slug });
        window.location.href = r.checkout_url;
      } catch (err) {
        toast.error(errMsg(err));
        setSaving(false);
      }
      return;
    }
    try {
      const { data: r } = await api.post("/public/application/submit", { session_token: session!.token, current_step: last, data, lang: localStorage.getItem("irs_lang") || "en" });
      localStorage.removeItem(STORE);
      setDone(r.reference);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setSaving(false);
  };

  if (done)
    return (
      <PublicShell>
        <div className="mx-auto max-w-xl px-4 pt-20">
          <div className="card-soft fade-up p-10 text-center" data-testid="application-submitted">
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-[#DCFCE7] text-[#15803D]"><CheckCircle2 className="h-8 w-8" /></span>
            <h1 className="mt-6 text-3xl font-extrabold">Thank you! Your application is submitted.</h1>
            <p className="mt-4 text-lg text-[#475569]">Our team will now complete your background screening. Your organization will receive the results. There's nothing else you need to do.</p>
            <p className="mt-4 rounded-xl bg-[#FFF6EE] px-4 py-3 text-sm text-[#7C2D12]" data-testid="application-status-note">We've emailed you a confirmation. Please note: Info Retrieval Services cannot answer questions about your report status. Always contact your organization with any questions.</p>
            <p className="mt-6 text-sm text-[#64748B]">Reference number: <span className="font-mono font-semibold text-[#1A2536]" data-testid="application-reference">{done}</span></p>
          </div>
        </div>
      </PublicShell>
    );

  if (slug && params.get("paid"))
    return (
      <PublicShell>
        <div className="mx-auto max-w-xl px-4 pt-20">
          <div className="card-soft p-10 text-center" data-testid="payment-confirming">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#1B365D]" />
            <h1 className="mt-6 text-2xl font-extrabold">Confirming your payment…</h1>
          </div>
        </div>
      </PublicShell>
    );

  if (!app) return <PublicShell>{slug ? <OrgGate slug={slug} onStart={startOrg} onCode={start} /> : <CodeGate onStart={start} />}</PublicShell>;

  const setC = (k: string) => (v: any) => setData((d: any) => ({ ...d, consent: { ...d.consent, [k]: v } }));
  const setW = (k: string) => (v: any) => setData((d: any) => ({ ...d, dow: { ...d.dow, [k]: v } }));
  const personal = <StepPersonal key="p" p={data.personal} short={isNch} onChange={(personal) => setData({ ...data, personal })} />;
  const addresses = <StepAddresses key="a" addrs={data.addresses} onChange={(addresses) => setData({ ...data, addresses })} />;
  const review = (
    <div key="r">
      <StepReview data={data} goTo={setStep} dow={isDow} />
      {app.payment_required && <StepPayment c={data.consent} fee={app.fee || 50} participation={isNch ? app.participation || app.org_name : undefined} onChange={(consent) => setData({ ...data, consent })} />}
    </div>
  );
  const bodies = isDow ? [
    <DowIntro key="i" w={data.dow} set={setW as any} />,
    <div key="p" className="space-y-6">
      {personal}
    </div>,
    addresses,
    <DowCourt key="c" c={data.consent} w={data.dow} locations={app.locations || []} locked={!!app.location_locked} setC={setC as any} set={setW as any} />,
    <DowAuth key="au" c={data.consent} w={data.dow} foreign={foreign} setC={setC as any} set={setW as any} />,
    review,
  ] : [
    <DowIntro key="i" w={data.dow} set={setW as any} org={app.org_name || "the organization"} codeAck={!!session?.code} />,
    personal,
    addresses,
    <StepConsent key="c" c={data.consent} w={data.dow} foreign={foreign} setC={setC as any} setW={setW as any} orgName={app.org_name} noCourt={isNch} stateAuth={!!app.state_auth} />,
    review,
  ];

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-4 pt-8 sm:px-6">
        {app.org_name && (
          <div className="fade-up relative mb-6 overflow-hidden rounded-2xl border border-[#F26B4F]/25 bg-[#FFF6EE] px-6 py-5" data-testid="application-org-banner">
            <span aria-hidden className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#FFB547]/30 blur-2xl" />
            <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 bg-[#F26B4F]" />
            <h2 className="relative text-2xl font-extrabold tracking-tight text-[#1B365D] sm:text-3xl" data-testid="application-org-name">{app.org_name}</h2>
            <p className="relative mt-0.5 text-base text-[#475569]">Background Check Online Application</p>
          </div>
        )}
        <Stepper step={step} labels={labels} />
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E8573A]">Step {step + 1} of {labels.length}</p>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight">{labels[step]}</h1>
          </div>
          <span className="inline-flex items-center gap-2 self-start rounded-full bg-[#FFF1CC] px-3 py-1.5 text-sm font-semibold text-[#9A5B00]" data-testid="application-time-left">
            <Clock className="h-4 w-4" /> {left} left to finish
          </span>
        </div>
        <div className="fade-up" key={step}>{bodies[step]}</div>
        <div className="mt-8 flex items-center justify-between gap-3">
          <button type="button" data-testid="wizard-prev-step-button" className="btn-secondary" disabled={step === 0 || saving} onClick={() => setStep(step - 1)}>
            <ArrowLeft className="h-5 w-5" /> Back
          </button>
          {step < last ? (
            <button type="button" data-testid="wizard-next-step-button" className="btn-primary min-w-[180px] bg-[#F26B4F] shadow-[0_12px_24px_-12px_rgba(242,107,79,0.7)] hover:bg-[#E0553A]" disabled={saving} onClick={next}>
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Save & continue <ArrowRight className="h-5 w-5" /></>}
            </button>
          ) : (
            <button type="button" data-testid="wizard-submit-button" className="btn-primary min-w-[200px] bg-[#15803D] hover:bg-[#166534]" disabled={saving} onClick={submit}>
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : app.payment_required ? <><Send className="h-5 w-5" /> Pay {money(app.fee || 50)} &amp; submit</> : <><Send className="h-5 w-5" /> Submit application</>}
            </button>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-[#94A3B8]">Your progress is saved each time you continue.</p>
      </div>
    </PublicShell>
  );
}
